import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { Grid, Typography } from '@mui/material';
import { DashboardCard, DashboardCardRow } from 'components/molecules/DashboardCard';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { simpleFormatDate } from 'utils/date';
import { EButtonSizesPX, EButtonVariants } from 'constants/Buttons';
import Button from 'components/atoms/Button';
import { useCaseDashboard } from './hooks';
import { EPeriod } from './helpers';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { useDictionary } from 'models/dictionaryContext';
import { FormMultiSelect } from 'components/molecules/FormMultiSelect';
import { useForm } from 'react-hook-form';
import { Loader } from 'components/atoms/Loader';
import { useAccount } from 'pages/AccountPage/hooks';

interface IDashboardCardData {
  data: any;
  title: string;
  totalColumn?: boolean;
  firstEmptyRow?: boolean;
  lastEmptyRow?: boolean;
}

const DashboardCardData = ({
  data,
  title,
  totalColumn = false,
  firstEmptyRow = false,
  lastEmptyRow = false,
}: IDashboardCardData) => {
  if (data) {
    const isFlat = typeof Object.values(data)[0] !== 'object';

    const columns = isFlat ? [] : Object.keys(data[Object.keys(data)[0]]);
    const rowsNames = Object.keys(data);
    const getValues = (name: string) =>
      isFlat ? [data[name]] : columns.map((col) => data[name][col]);

    const rows: DashboardCardRow[] = rowsNames.map((name: string) => ({
      name: `caseManagement.dashboard.${name.toLowerCase()}.label`,
      columns: getValues(name),
    }));
    return (
      <DashboardCard
        title={title}
        columnHeaders={columns.map(
          (col) => `caseManagement.dashboard.${col.toLowerCase()}.label`,
        )}
        rows={rows}
        totalColumn={totalColumn}
        firstEmptyRow={firstEmptyRow}
        lastEmptyRow={lastEmptyRow}
      />
    );
  }
};

interface IInitialFormValues {
  licenceAuthorityUniqueIds: string[];
}

const initialFormValues: IInitialFormValues = {
  licenceAuthorityUniqueIds: [],
};

const Dashboard = () => {
  const { t } = useTranslations();
  const [isLoading, setIsLoading] = useState(false);
  const { shortDateFormat, getProperties } = useGlobalProperty();
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [selectedPeriodOption, setSelectedPeriodOption] = useState<EPeriod>(EPeriod.today);
  const { getDashboardData, dashboardData } = useCaseDashboard();
  const { weaponLicencingAuthority, getWeaponLicencingAuthority } = useDictionary();
  const [licencingAuthorityIsLoading, setLicencingAuthorityIsLoading] = useState(true);
  const { account, getSelfAccountData } = useAccount();

  useEffect(() => {
    getSelfAccountData();
  }, []);

  const {
    control,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: initialFormValues,
  });
  const licenceAuthorityUniqueIds = watch('licenceAuthorityUniqueIds');

  useEffect(() => {
    if (!weaponLicencingAuthority) {
      getWeaponLicencingAuthority();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (weaponLicencingAuthority && !licenceAuthorityUniqueIds.length && account) {
      const values = weaponLicencingAuthority
        .filter((licencingAuthority) =>
          account?.licenceAuthorityIds.includes(licencingAuthority.uniqueId.toString()),
        )
        .map((item) => item.uniqueId.toString());

      setValue('licenceAuthorityUniqueIds', values);
      setLicencingAuthorityIsLoading(false);
    }
  }, [weaponLicencingAuthority, account]);

  useEffect(() => {
    if (licenceAuthorityUniqueIds.length) {
      clearErrors();
    } else {
      setError?.('licenceAuthorityUniqueIds', {
        type: 'required',
        message: t('general.field.validation.required'),
      });
    }
  }, [licenceAuthorityUniqueIds]);

  useEffect(() => {
    getProperties();
  }, []);

  useEffect(() => {
    (async function init() {
      setIsLoading(true);
      if (licenceAuthorityUniqueIds.length) {
        await getDashboardData(selectedPeriodOption, licenceAuthorityUniqueIds);
        setIsLoading(false);
      }
    })();
  }, [selectedPeriodOption, licenceAuthorityUniqueIds]);

  useEffect(() => {
    const currentDate = new Date();
    switch (selectedPeriodOption) {
      case EPeriod.today:
        setFromDate(new Date());
        setToDate(new Date());
        break;
      case EPeriod.last7days:
        setFromDate(new Date(new Date().setDate(currentDate.getDate() - 7)));
        setToDate(new Date());
        break;
      case EPeriod.lastMonth:
        setFromDate(new Date(new Date().setMonth(currentDate.getMonth() - 1)));
        setToDate(new Date());
        break;
      default:
        break;
    }
  }, [selectedPeriodOption]);

  return (
    <Grid container columnSpacing="20px" sx={{ rowGap: '20px' }}>
      <Grid
        container
        size={{ xs: 12 }}
        sx={{
          display: 'flex',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          flexDirection: { xs: 'column-reverse', md: 'row' },
          padding: 0,
          height: { xs: 'auto', md: '40px' },
          justifyContent: 'space-between',
          alignItems: 'start',
          rowGap: { xs: 2, sm: 1 },
        }}
      >
        <Grid
          size={{ xs: 12, md: 'auto' }}
          sx={{ justifyItems: { xs: 'center', sm: 'left' }, alignSelf: 'center' }}
        >
          <Typography variant="body2">
            {`${t('caseManagement.dashboard.period.label')}: ${simpleFormatDate(fromDate, shortDateFormat)} - ${simpleFormatDate(toDate, shortDateFormat)}`}
          </Typography>
        </Grid>
        <Grid
          size={{ xs: 12, md: 'auto' }}
          container
          columnSpacing="4px"
          sx={{
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            rowGap: 1,
          }}
        >
          <Grid
            container
            columnSpacing="4px"
            size={{ xs: 12, md: 'auto' }}
            sx={{
              flexWrap: 'nowrap',
            }}
          >
            <Grid>
              <Button
                variant={EButtonVariants.text}
                sizePX={EButtonSizesPX.px40}
                label={t('caseManagement.dashboard.today.label')}
                onClick={() => {
                  setSelectedPeriodOption(EPeriod.today);
                }}
                sx={{
                  fontWeight: selectedPeriodOption === EPeriod.today ? '900 !important' : 500,
                }}
                id="dashboard_today-button"
              />
            </Grid>
            <Grid>
              <Button
                variant={EButtonVariants.text}
                sizePX={EButtonSizesPX.px40}
                label={t('caseManagement.dashboard.last7days.label')}
                onClick={() => {
                  setSelectedPeriodOption(EPeriod.last7days);
                }}
                sx={{
                  fontWeight:
                    selectedPeriodOption === EPeriod.last7days ? '900 !important' : 500,
                }}
                id="dashboard_last7days-button"
              />
            </Grid>
            <Grid>
              <Button
                variant={EButtonVariants.text}
                sizePX={EButtonSizesPX.px40}
                label={t('caseManagement.dashboard.lastMonth.label')}
                onClick={() => {
                  setSelectedPeriodOption(EPeriod.lastMonth);
                }}
                sx={{
                  fontWeight:
                    selectedPeriodOption === EPeriod.lastMonth ? '900 !important' : 500,
                }}
                id="dashboard_last_month-button"
              />
            </Grid>
          </Grid>
          <Grid size={{ xs: 12, md: 'auto' }} sx={{ width: { xs: '100%', sm: '180px' } }}>
            {licencingAuthorityIsLoading ? (
              <Loader isLoading my={1.5} />
            ) : (
              <FormMultiSelect
                options={mapDictionaryToOptionProp(weaponLicencingAuthority)}
                name="licenceAuthorityUniqueIds"
                setValue={setValue}
                control={control}
                errors={errors}
                canBeEmpty={false}
                atLeastOneRequired
                withValidation={false}
              />
            )}
          </Grid>
          {/* <Grid ml={2}>
            <Button
              variant={EButtonVariants.outlined}
              sizePX={EButtonSizesPX.px40}
              icon={EIconTypes.filters2}
              onClick={() => {}}
              id="dashboard_filters2-button"
            />
          </Grid> */}
        </Grid>
      </Grid>
      {isLoading ? (
        <Grid size={{ xs: 12 }}>
          <Loader isLoading={true} />
        </Grid>
      ) : (
        <>
          {dashboardData?.caseStatistics && (
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{ maxWidth: 'calc(100vw - 48px)', overflow: 'auto' }}
            >
              <DashboardCardData
                data={dashboardData?.caseStatistics?.caseStatistics}
                title={t('caseManagement.dashboard.cases.label')}
              />
            </Grid>
          )}
          {dashboardData?.otherRequestStatistics && (
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{ maxWidth: 'calc(100vw - 48px)', overflow: 'auto' }}
            >
              <DashboardCardData
                data={dashboardData?.otherRequestStatistics}
                title={t('caseManagement.dashboard.otherRequests.label')}
                firstEmptyRow
              />
            </Grid>
          )}
          {dashboardData?.caseProcessStatistics && (
            <Grid size={{ xs: 12 }} sx={{ maxWidth: 'calc(100vw - 48px)', overflow: 'auto' }}>
              <DashboardCardData
                data={dashboardData?.caseProcessStatistics?.processStatistics}
                title={t('caseManagement.dashboard.caseProcessProgress.label')}
                totalColumn
              />
            </Grid>
          )}
          {dashboardData?.licenceStatusStatistics && (
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{ maxWidth: 'calc(100vw - 48px)', overflow: 'auto' }}
            >
              <DashboardCardData
                data={dashboardData?.licenceStatusStatistics.licenceStatusStatistics}
                title={t('caseManagement.dashboard.licenceStatuses.label')}
              />
            </Grid>
          )}
          {dashboardData?.licenceAlertStatistics && (
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{ maxWidth: 'calc(100vw - 48px)', overflow: 'auto' }}
            >
              <DashboardCardData
                data={dashboardData?.licenceAlertStatistics}
                title={t('caseManagement.dashboard.licenceAlerts.label')}
                lastEmptyRow
              />
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
};

export default Dashboard;
